/**
 * Outil « Calcul malin » — stratégies de calcul mental (pur).
 *
 * Pour a + b, a − b et a × b (entiers de 1 à 999), chaque stratégie dit si
 * elle s'applique, à partir de quel niveau, et produit ses étapes (LaTeX +
 * une phrase), le résultat et de quoi dessiner : des sauts sur une droite
 * (addition, soustraction), deux écarts égaux (écart constant), une grille
 * de produits partiels signés ou un modèle d'aire (multiplication).
 * Rangs : 6e = 1, 5e = 2, 4e = 3, 3e = 4.
 *
 *   de tête                  8 + 9, 100 + 39, 999 − 1, 7 × 8, 49 × 10 (et rien d'autre)
 *   passer la dizaine        38 + 7 = 38 + 2 + 5 ;  82 − 7 = 82 − 2 − 5
 *   compléter                201 − 198 : 198 → 200 → 201, l'écart est 2 + 1
 *   ajouter par rangs        39 + 52 = 39 + 50 + 2
 *   arrondir, compenser      39 + 52 = 52 + 40 − 1 ;  82 − 39 = 82 − 40 + 1
 *   arrondir les deux        39 + 52 = 40 + 50 + 2 − 1  (écarts appliqués un à un : pas de négatif en 6e)
 *   écart constant           82 − 39 = 83 − 40
 *   découper (rangs)         23 × 14 = (20 + 3)(10 + 4)
 *   arrondir un facteur      99 × 37 = 100 × 37 − 37
 *   ×5, ×25, ×50             36 × 25 = 3 600 ÷ 4
 *   double distributivité    49 × 19 = (50 − 1)(20 − 1) = 1 000 − 50 − 20 + 1   (4e)
 *   identités remarquables   51² = 2 500 + 100 + 1 ; 49 × 51 = 2 500 − 1        (3e)
 *
 * Seule une compensation « vers le bas » (39 = 40 − 1) est proposée pour + et − :
 * 52 = 50 + 2 n'est que la méthode par rangs. L'ordre de préférence (la méthode
 * conseillée en premier) est fixé par `plansFor`.
 */

export type Op = 'add' | 'sub' | 'mul';

export type StrategyId =
  | 'direct'
  | 'bridge'
  | 'complete'
  | 'ranks'
  | 'round'
  | 'round-both'
  | 'shift'
  | 'split'
  | 'round-one'
  | 'quarter'
  | 'double-round'
  | 'square'
  | 'diff-squares';

export interface Step {
  tex: string;
  say: string;
}

export interface Plan {
  id: StrategyId;
  name: string;
  steps: Step[];
  result: number;
  /**
   * Addition, soustraction : départ et sauts successifs (signés). Le saut k
   * apparaît avec l'étape `firstJumpStep + k`. `gap` : on cherche la longueur
   * des sauts (compléter), pas le point d'arrivée.
   */
  line?: { start: number; jumps: number[]; firstJumpStep: number; gap?: boolean };
  /** écart constant : [b, a] puis [b + s, a + s] */
  shift?: { from: [number, number]; to: [number, number] };
  /** multiplication : morceaux signés des deux facteurs (grille de produits partiels) */
  grid?: { rows: number[]; cols: number[] };
  /** (m − d)(n − e) : le rectangle m × n dont on retire deux bandes qui se recouvrent */
  area?: { rows: [number, number]; cols: [number, number] };
}

export interface StrategyDef {
  id: StrategyId;
  op: Op;
  minRank: number;
  plan: (a: number, b: number, rank: number) => Plan | null;
}

export const MIN = 1;
export const MAX = 999;

const NNBSP = String.fromCharCode(0x202f);

/** nombre « rond » le plus proche (centaine si à 2 près, sinon dizaine) : n = m + d */
export function roundOf(n: number): { m: number; d: number } {
  const h = Math.round(n / 100) * 100;
  if (h > 0 && Math.abs(n - h) <= 2) return { m: h, d: n - h };
  const t = Math.round(n / 10) * 10;
  return { m: t, d: n - t };
}

/** proche d'un nombre rond (à 1 ou 2 près), sans en être un */
export function isNear(n: number): boolean {
  const { m, d } = roundOf(n);
  return m > 0 && d !== 0 && Math.abs(d) <= 2;
}

/** 1, 10, 100, 1 000 */
const isPow10 = (n: number) => n === 1 || n === 10 || n === 100 || n === 1000;

/** « 1 000 » : espace fine insécable entre les milliers (texte) */
export const fmt = (n: number) =>
  (n < 0 ? '−' : '') + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
const tx = (n: number) =>
  (n < 0 ? '-' : '') + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\\,');
/** « + 2 », « - 1 » dans une somme LaTeX */
const signed = (n: number) => (n < 0 ? ` - ${tx(-n)}` : ` + ${tx(n)}`);
/** « m − 1 » ou « m + 2 » écrit en LaTeX */
const near = (m: number, d: number) => `${tx(m)}${signed(d)}`;
/** « 40 − 1 » en texte, insécable */
const nearText = (m: number, d: number) =>
  `${fmt(m)}${NNBSP}${d < 0 ? '−' : '+'}${NNBSP}${fmt(Math.abs(d))}`;
/** accord : « on l'enlève » / « on les enlève » */
const them = (n: number, one: string, many: string) => (Math.abs(n) > 1 ? many : one);

/** découpage par rangs : 245 → [200, 40, 5] (sans les zéros) */
export function placeParts(n: number): number[] {
  const parts: number[] = [];
  let p = 1;
  let rest = n;
  while (rest > 0) {
    const digit = rest % 10;
    if (digit) parts.unshift(digit * p);
    rest = Math.floor(rest / 10);
    p *= 10;
  }
  return parts.length ? parts : [0];
}

const digits = (n: number) => String(n).split('').reverse().map(Number);
/** a + b sans retenue (chiffre par chiffre) */
function noCarry(a: number, b: number): boolean {
  const [x, y] = [digits(a), digits(b)];
  return x.every((d, i) => d + (y[i] ?? 0) < 10) && y.every((d, i) => d + (x[i] ?? 0) < 10);
}
/** a − b sans retenue : chaque chiffre de a au moins celui de b */
function noBorrow(a: number, b: number): boolean {
  const [x, y] = [digits(a), digits(b)];
  return y.every((d, i) => (x[i] ?? 0) >= d);
}

/** 300 → 3 × 100 (un seul chiffre non nul) */
function tenPower(n: number): { d: number; p: number } {
  let p = 1;
  while (n % (p * 10) === 0) p *= 10;
  return { d: n / p, p };
}

/* ───────────── de tête : quand c'est immédiat, aucune « astuce » ───────────── */

const DIRECT_NAME = 'De tête';

function direct(op: Op, a: number, b: number): Plan | null {
  const plan = (say: string): Plan => ({
    id: 'direct',
    name: DIRECT_NAME,
    result: result(op, a, b),
    steps: [{ tex: `${tx(a)} ${OP_TEX[op]} ${tx(b)} = ${tx(result(op, a, b))}`, say }],
  });
  if (op === 'add') {
    if (a < 10 && b < 10) return plan('C’est dans les tables d’addition.');
    if ((a % 10 === 0 || b % 10 === 0) && noCarry(a, b))
      return plan('Pas de retenue : on ajoute rang par rang, directement.');
    return null;
  }
  if (op === 'sub') {
    if (b > a) return null;
    if (a === b) return plan('Un nombre moins lui-même : il ne reste rien.');
    if (a < 20 && b < 10) return plan('C’est dans les tables d’addition, à l’envers.');
    if ((b < 10 || b % 10 === 0) && noBorrow(a, b))
      return plan('Pas de retenue : on enlève rang par rang, directement.');
    return null;
  }
  if (a <= 10 && b <= 10) return plan('C’est dans les tables de multiplication.');
  if (a === 1 || b === 1) return plan('Multiplier par 1 ne change rien.');
  const p = isPow10(a) ? a : isPow10(b) ? b : 0;
  if (p)
    return plan(
      `Multiplier par ${fmt(p)} : chaque chiffre prend une valeur ${fmt(p)} fois plus grande (les unités deviennent des ${p === 10 ? 'dizaines' : p === 100 ? 'centaines' : 'milliers'}).`,
    );
  return null;
}

/* ───────────── addition ───────────── */

/** la meilleure compensation « vers le bas » parmi les nombres donnés (≥ 10) */
function roundDown(...xs: number[]): number | null {
  const ok = xs.filter((x) => x >= 10 && isNear(x) && roundOf(x).d < 0);
  ok.sort((x, y) => Math.abs(roundOf(x).d) - Math.abs(roundOf(y).d));
  return ok[0] ?? null;
}

const ADD_BRIDGE: StrategyDef = {
  id: 'bridge',
  op: 'add',
  minRank: 1,
  plan: (a, b) => {
    const [x, s] = a >= 10 && b < 10 ? [a, b] : b >= 10 && a < 10 ? [b, a] : [0, 0];
    if (!x || x % 10 === 0 || (x % 10) + s < 10) return null;
    const c = 10 - (x % 10);
    const ten = x + c;
    const rest = s - c;
    return {
      id: 'bridge',
      name: 'Passer la dizaine',
      result: a + b,
      line: { start: x, jumps: rest ? [c, rest] : [c], firstJumpStep: rest ? 1 : 0 },
      steps: [
        ...(rest
          ? [
              {
                tex: `${s} = ${c} + ${rest}`,
                say: `Pour aller de ${fmt(x)} à la dizaine ${fmt(ten)}, il faut ${c}.`,
              },
            ]
          : []),
        { tex: `${tx(x)} + ${c} = ${tx(ten)}`, say: 'On complète la dizaine.' },
        ...(rest ? [{ tex: `${tx(ten)} + ${rest} = ${tx(a + b)}`, say: 'Puis le reste.' }] : []),
      ],
    };
  },
};

const ADD_RANKS: StrategyDef = {
  id: 'ranks',
  op: 'add',
  minRank: 1,
  plan: (a, b) => {
    const parts = placeParts(b);
    const steps: Step[] =
      parts.length > 1
        ? [
            {
              tex: `${tx(a)} + ${tx(b)} = ${tx(a)} + ${parts.map(tx).join(' + ')}`,
              say: `On découpe ${fmt(b)} par rangs : ${parts.map(fmt).join(' + ')}.`,
            },
          ]
        : [];
    let acc = a;
    parts.forEach((p, i) => {
      steps.push({
        tex: `${tx(acc)} + ${tx(p)} = ${tx(acc + p)}`,
        say:
          parts.length === 1
            ? 'On ajoute directement.'
            : i === 0
              ? 'On ajoute le plus gros morceau d’abord.'
              : 'Puis le morceau suivant.',
      });
      acc += p;
    });
    return {
      id: 'ranks',
      name: 'Ajouter par rangs',
      steps,
      result: a + b,
      line: { start: a, jumps: parts, firstJumpStep: parts.length > 1 ? 1 : 0 },
    };
  },
};

const ADD_ROUND: StrategyDef = {
  id: 'round',
  op: 'add',
  minRank: 1,
  plan: (a, b) => {
    const x = roundDown(a, b);
    if (x === null) return null;
    const other = x === a ? b : a;
    if (other < 10) return null;
    const r = roundOf(x);
    const big = r.m + other;
    return {
      id: 'round',
      name: 'Arrondir et compenser',
      result: a + b,
      line: { start: other, jumps: [r.m, r.d], firstJumpStep: 1 },
      steps: [
        { tex: `${tx(x)} = ${near(r.m, r.d)}`, say: `${fmt(x)} est tout près de ${fmt(r.m)}.` },
        {
          tex: `${tx(other)} + ${tx(r.m)} = ${tx(big)}`,
          say: `On part de ${fmt(other)} et on ajoute ${fmt(r.m)} : c’est facile, c’est un nombre rond.`,
        },
        {
          tex: `${tx(big)} - ${tx(-r.d)} = ${tx(a + b)}`,
          say: `On a ajouté ${fmt(-r.d)} de trop : on ${them(r.d, 'l’enlève', 'les enlève')}.`,
        },
      ],
    };
  },
};

const ADD_ROUND_BOTH: StrategyDef = {
  id: 'round-both',
  op: 'add',
  minRank: 1,
  plan: (a, b) => {
    if (a < 10 || b < 10 || !isNear(a) || !isNear(b)) return null;
    const ra = roundOf(a);
    const rb = roundOf(b);
    if (ra.d > 0 && rb.d > 0) return null; // 52 + 41 : c'est la méthode par rangs
    const rounds = ra.m + rb.m;
    // écarts appliqués UN À UN, le positif d'abord : jamais de nombre négatif (6e)
    const gaps = [
      { d: ra.d, n: a },
      { d: rb.d, n: b },
    ].sort((x, y) => y.d - x.d);
    const ds = gaps.map((g) => g.d);
    const steps: Step[] = [
      {
        tex: `${tx(a)} + ${tx(b)} = ${tx(ra.m)} + ${tx(rb.m)}${ds.map(signed).join('')}`,
        say: `On arrondit les deux : ${fmt(a)} = ${nearText(ra.m, ra.d)} et ${fmt(b)} = ${nearText(rb.m, rb.d)}.`,
      },
      { tex: `${tx(ra.m)} + ${tx(rb.m)} = ${tx(rounds)}`, say: 'Les nombres ronds d’abord.' },
    ];
    let acc = rounds;
    for (const { d, n } of gaps) {
      steps.push({
        tex: `${tx(acc)}${signed(d)} = ${tx(acc + d)}`,
        say:
          d > 0
            ? `Puis on ajoute le ${fmt(d)} qui reste de ${fmt(n)}.`
            : `Pour ${fmt(n)}, on a pris ${fmt(-d)} de trop : on ${them(d, 'l’enlève', 'les enlève')}.`,
      });
      acc += d;
    }
    return {
      id: 'round-both',
      name: 'Arrondir les deux',
      result: a + b,
      line: { start: ra.m, jumps: [rb.m, ...ds], firstJumpStep: 1 },
      steps,
    };
  },
};

/* ───────────── soustraction ───────────── */

const SUB_BRIDGE: StrategyDef = {
  id: 'bridge',
  op: 'sub',
  minRank: 1,
  plan: (a, b) => {
    if (b >= 10 || a < 10 || a % 10 === 0 || a % 10 >= b) return null;
    const c = a % 10;
    const ten = a - c;
    const rest = b - c;
    return {
      id: 'bridge',
      name: 'Passer la dizaine',
      result: a - b,
      line: { start: a, jumps: [-c, -rest], firstJumpStep: 1 },
      steps: [
        {
          tex: `${b} = ${c} + ${rest}`,
          say: `Pour redescendre de ${fmt(a)} à la dizaine ${fmt(ten)}, on enlève ${c}.`,
        },
        { tex: `${tx(a)} - ${c} = ${tx(ten)}`, say: 'On redescend à la dizaine.' },
        { tex: `${tx(ten)} - ${rest} = ${tx(a - b)}`, say: 'Puis on enlève le reste.' },
      ],
    };
  },
};

/** pour compléter : le nombre rond juste au-dessus de b (centaine si elle est proche) */
function nextRound(b: number): number {
  const h = Math.ceil((b + 1) / 100) * 100;
  if (h - b <= 10) return h;
  return Math.ceil((b + 1) / 10) * 10;
}

const SUB_COMPLETE: StrategyDef = {
  id: 'complete',
  op: 'sub',
  minRank: 1,
  plan: (a, b) => {
    if (b < 10 || b >= a || a - b > 30) return null;
    const r = nextRound(b);
    const jumps = r < a ? [r - b, a - r] : [a - b];
    const steps: Step[] = [
      {
        tex: `${tx(b)} + \\;?\\; = ${tx(a)}`,
        say: `Soustraire, c’est chercher l’écart : combien faut-il ajouter à ${fmt(b)} pour arriver à ${fmt(a)} ?`,
      },
    ];
    let acc = b;
    jumps.forEach((j, i) => {
      steps.push({
        tex: `${tx(acc)} + ${tx(j)} = ${tx(acc + j)}`,
        say: i === 0 && jumps.length > 1 ? 'Jusqu’au nombre rond.' : `Jusqu’à ${fmt(a)}.`,
      });
      acc += j;
    });
    steps.push({
      tex:
        jumps.length > 1
          ? `${tx(a)} - ${tx(b)} = ${jumps.map(tx).join(' + ')} = ${tx(a - b)}`
          : `${tx(a)} - ${tx(b)} = ${tx(a - b)}`,
      say: jumps.length > 1 ? 'L’écart, c’est la somme des sauts.' : 'L’écart, c’est ce saut.',
    });
    return {
      id: 'complete',
      name: 'Compléter',
      result: a - b,
      line: { start: b, jumps, firstJumpStep: 1, gap: true },
      steps,
    };
  },
};

const SUB_RANKS: StrategyDef = {
  id: 'ranks',
  op: 'sub',
  minRank: 1,
  plan: (a, b) => {
    if (b > a) return null;
    const parts = placeParts(b);
    const steps: Step[] =
      parts.length > 1
        ? [
            {
              tex: `${tx(a)} - ${tx(b)} = ${tx(a)} - ${parts.map(tx).join(' - ')}`,
              say: `On découpe ${fmt(b)} par rangs : ${parts.map(fmt).join(' + ')}, et on enlève morceau par morceau.`,
            },
          ]
        : [];
    let acc = a;
    parts.forEach((p) => {
      steps.push({
        tex: `${tx(acc)} - ${tx(p)} = ${tx(acc - p)}`,
        say: parts.length > 1 ? 'Un morceau de moins.' : 'On enlève directement.',
      });
      acc -= p;
    });
    return {
      id: 'ranks',
      name: 'Enlever par rangs',
      steps,
      result: a - b,
      line: { start: a, jumps: parts.map((p) => -p), firstJumpStep: parts.length > 1 ? 1 : 0 },
    };
  },
};

const SUB_ROUND: StrategyDef = {
  id: 'round',
  op: 'sub',
  minRank: 1,
  plan: (a, b) => {
    if (b > a) return null;
    // b juste sous un nombre rond : 82 − 39 = 82 − 40 + 1
    if (b >= 10 && isNear(b) && roundOf(b).d < 0) {
      const r = roundOf(b);
      if (r.m > a) return null;
      const mid = a - r.m;
      return {
        id: 'round',
        name: 'Arrondir et compenser',
        result: a - b,
        line: { start: a, jumps: [-r.m, -r.d], firstJumpStep: 1 },
        steps: [
          { tex: `${tx(b)} = ${near(r.m, r.d)}`, say: `${fmt(b)} est tout près de ${fmt(r.m)}.` },
          {
            tex: `${tx(a)} - ${tx(r.m)} = ${tx(mid)}`,
            say: `On enlève ${fmt(r.m)} : c’est facile, c’est un nombre rond.`,
          },
          {
            tex: `${tx(mid)} + ${tx(-r.d)} = ${tx(a - b)}`,
            say: `On a enlevé ${fmt(-r.d)} de trop : on ${them(r.d, 'le rajoute', 'les rajoute')}.`,
          },
        ],
      };
    }
    // a près d'un nombre rond : 98 − 35 = 100 − 35 − 2 ; 101 − 35 = 100 − 35 + 1
    if (a >= 10 && isNear(a) && b >= 10) {
      const r = roundOf(a);
      if (b > r.m) return null;
      const mid = r.m - b;
      return {
        id: 'round',
        name: 'Arrondir et compenser',
        result: a - b,
        line: { start: r.m, jumps: [-b, r.d], firstJumpStep: 1 },
        steps: [
          { tex: `${tx(a)} = ${near(r.m, r.d)}`, say: `${fmt(a)} est tout près de ${fmt(r.m)}.` },
          { tex: `${tx(r.m)} - ${tx(b)} = ${tx(mid)}`, say: `On part de ${fmt(r.m)}.` },
          {
            tex: `${tx(mid)}${signed(r.d)} = ${tx(a - b)}`,
            say:
              r.d < 0
                ? `On est parti de ${fmt(-r.d)} de trop : on ${them(r.d, 'l’enlève', 'les enlève')}.`
                : `On est parti de ${fmt(r.d)} de moins : on ${them(r.d, 'l’ajoute', 'les ajoute')}.`,
          },
        ],
      };
    }
    return null;
  },
};

const SUB_SHIFT: StrategyDef = {
  id: 'shift',
  op: 'sub',
  minRank: 1,
  plan: (a, b) => {
    if (b > a || b < 10 || !isNear(b)) return null;
    const s = -roundOf(b).d;
    const [a2, b2] = [a + s, b + s];
    return {
      id: 'shift',
      name: 'Écart constant',
      result: a - b,
      shift: { from: [b, a], to: [b2, a2] },
      steps: [
        {
          tex: `${tx(a)} - ${tx(b)} = ${tx(a2)} - ${tx(b2)}`,
          say: `On ${s > 0 ? 'ajoute' : 'enlève'} ${fmt(Math.abs(s))} aux deux nombres : l’écart entre eux ne change pas.`,
        },
        { tex: `${tx(a2)} - ${tx(b2)} = ${tx(a - b)}`, say: 'On enlève un nombre rond.' },
      ],
    };
  },
};

/* ───────────── multiplication ───────────── */

/** « (−1) » entre parenthèses quand il est négatif */
const factorTex = (x: number) => (x < 0 ? `(${tx(x)})` : tx(x));

/** les produits partiels d'une grille, ligne par ligne */
function partials(rows: number[], cols: number[]): number[] {
  return rows.flatMap((r) => cols.map((c) => r * c));
}

/** « 200 + 80 - 30 + 12 » */
const sumTex = (xs: number[]) => xs.map((x, i) => (i === 0 ? tx(x) : signed(x))).join('');
/** chaque produit groupé : KaTeX ne coupe la ligne qu'entre deux termes */
const productsTex = (rows: number[], cols: number[]) =>
  rows.flatMap((r) => cols.map((c) => `{${factorTex(r)} \\times ${factorTex(c)}}`)).join(' + ');

const MUL_SPLIT: StrategyDef = {
  id: 'split',
  op: 'mul',
  minRank: 1,
  plan: (a, b) => {
    const rows = placeParts(a);
    const cols = placeParts(b);
    if (rows.length * cols.length < 2) {
      // un seul morceau : 30 × 40 = 3 × 4 × 100 (jamais « on ajoute des zéros »)
      const pa = tenPower(a);
      const pb = tenPower(b);
      const p10 = pa.p * pb.p;
      return {
        id: 'split',
        name: 'Découper',
        result: a * b,
        steps: [
          p10 > 1
            ? {
                tex: `${tx(a)} \\times ${tx(b)} = ${pa.d} \\times ${pb.d} \\times ${tx(p10)} = ${pa.d * pb.d} \\times ${tx(p10)} = ${tx(a * b)}`,
                say: `On calcule ${pa.d} × ${pb.d}, puis on multiplie par ${fmt(p10)}.`,
              }
            : { tex: `${tx(a)} \\times ${tx(b)} = ${tx(a * b)}`, say: 'C’est dans les tables.' },
        ],
      };
    }
    const ps = partials(rows, cols);
    const group = (xs: number[]) => (xs.length > 1 ? `(${xs.map(tx).join(' + ')})` : tx(xs[0]!));
    return {
      id: 'split',
      name: 'Découper',
      result: a * b,
      grid: { rows, cols },
      steps: [
        {
          tex: `${tx(a)} \\times ${tx(b)} = ${group(rows)} \\times ${group(cols)}`,
          say: 'On découpe par rangs (unités, dizaines, centaines).',
        },
        {
          tex: `= ${productsTex(rows, cols)}`,
          say: 'Chaque morceau de l’un multiplie chaque morceau de l’autre : une case de la grille chacun.',
        },
        { tex: `= ${sumTex(ps)}`, say: 'On calcule chaque case.' },
        { tex: `= ${tx(a * b)}`, say: 'On ajoute les cases.' },
      ],
    };
  },
};

/**
 * Le facteur à arrondir (≥ 10, ou 9 = 10 − 1) : d'abord celui qui devient une
 * puissance de 10 (101 → 100), puis une compensation vers le bas, puis le plus proche.
 */
function roundFactor(a: number, b: number): { x: number; k: number } | null {
  const ok = (n: number, other: number) => isNear(n) && (n >= 10 || n === 9) && other >= 10;
  const cands = [...(ok(a, b) ? [{ x: a, k: b }] : []), ...(ok(b, a) ? [{ x: b, k: a }] : [])];
  const score = (n: number) => {
    const r = roundOf(n);
    return (isPow10(r.m) ? 0 : 10) + (r.d < 0 ? 0 : 5) + Math.abs(r.d);
  };
  cands.sort((p, q) => score(p.x) - score(q.x));
  return cands[0] ?? null;
}

const MUL_ROUND_ONE: StrategyDef = {
  id: 'round-one',
  op: 'mul',
  minRank: 1,
  plan: (a, b, rank) => {
    const pick = roundFactor(a, b);
    if (!pick) return null;
    const { x, k } = pick;
    const r = roundOf(x);
    const big = r.m * k;
    const small = Math.abs(r.d) * k;
    const rows = [r.m, r.d];
    return {
      id: 'round-one',
      name: 'Arrondir un facteur',
      result: a * b,
      grid: x === a ? { rows, cols: [k] } : { rows: [k], cols: rows },
      steps: [
        { tex: `${tx(x)} = ${near(r.m, r.d)}`, say: `${fmt(x)} est tout près de ${fmt(r.m)}.` },
        {
          tex: `(${near(r.m, r.d)}) \\times ${tx(k)} = ${tx(r.m)} \\times ${tx(k)}${r.d < 0 ? ' - ' : ' + '}${tx(Math.abs(r.d))} \\times ${tx(k)}`,
          say:
            rank >= 2
              ? 'Distributivité : on multiplie chaque morceau par l’autre facteur.'
              : `${fmt(r.m)} fois ${fmt(k)}, puis on corrige de ${fmt(Math.abs(r.d))} fois ${fmt(k)}.`,
        },
        {
          tex: `= ${tx(big)}${r.d < 0 ? ' - ' : ' + '}${tx(small)}`,
          say: `${fmt(r.m)} × ${fmt(k)} est facile : c’est un nombre rond.`,
        },
        {
          tex: `= ${tx(a * b)}`,
          say:
            r.d < 0
              ? `On enlève les ${fmt(small)} comptés en trop.`
              : `On ajoute les ${fmt(small)} qui manquaient.`,
        },
      ],
    };
  },
};

/** ×5 = ×10 ÷ 2, ×25 = ×100 ÷ 4, ×50 = ×100 ÷ 2 */
const QUARTERS: Record<number, { m: number; q: number }> = {
  5: { m: 10, q: 2 },
  25: { m: 100, q: 4 },
  50: { m: 100, q: 2 },
};

const MUL_QUARTER: StrategyDef = {
  id: 'quarter',
  op: 'mul',
  minRank: 1,
  plan: (a, b) => {
    const [k, other] = QUARTERS[b] ? [b, a] : QUARTERS[a] ? [a, b] : [0, 0];
    if (!k || QUARTERS[other] || other <= 4 || (k === 5 && other <= 10)) return null;
    const { m, q } = QUARTERS[k]!;
    const big = other * m;
    const half = big / 2;
    return {
      id: 'quarter',
      name: '×5, ×25, ×50',
      result: a * b,
      steps: [
        {
          tex: `${tx(k)} = ${tx(m)} \\div ${q}`,
          say: `${fmt(k)}, c’est ${fmt(m)} partagé en ${q}.`,
        },
        {
          tex: `${tx(other)} \\times ${tx(m)} = ${tx(big)}`,
          say: `On multiplie par ${fmt(m)}.`,
        },
        q === 4
          ? {
              tex: `${tx(big)} \\div 4 = ${tx(big)} \\div 2 \\div 2 = ${tx(half)} \\div 2 = ${tx(a * b)}`,
              say: 'Diviser par 4, c’est prendre la moitié de la moitié.',
            }
          : { tex: `${tx(big)} \\div 2 = ${tx(a * b)}`, say: 'On prend la moitié.' },
      ],
    };
  },
};

/** 51², 49² (3e) : (m ± d)² */
function squareCase(a: number, b: number): { m: number; d: number } | null {
  if (a !== b || a < 10) return null;
  const r = roundOf(a);
  return r.d !== 0 && Math.abs(r.d) <= 3 && r.m > 0 ? r : null;
}

/** 49 × 51, 48 × 52 (3e) : (m − e)(m + e) */
function diffSquaresCase(a: number, b: number): { m: number; e: number } | null {
  if (a === b || (a + b) % 2 !== 0) return null;
  const m = (a + b) / 2;
  const e = Math.abs(a - b) / 2;
  return m % 10 === 0 && e <= 5 ? { m, e } : null;
}

const MUL_DOUBLE_ROUND: StrategyDef = {
  id: 'double-round',
  op: 'mul',
  minRank: 3,
  plan: (a, b, rank) => {
    if (a < 10 || b < 10 || !isNear(a) || !isNear(b)) return null;
    const ra = roundOf(a);
    const rb = roundOf(b);
    if (ra.d > 0 && rb.d > 0) return null; // (20 + 1)(30 + 2) : c'est « Découper »
    // en 3e, les identités remarquables font mieux sur ces cas-là
    if (rank >= 4 && (squareCase(a, b) || diffSquaresCase(a, b))) return null;
    const rows = [ra.m, ra.d];
    const cols = [rb.m, rb.d];
    const ps = partials(rows, cols);
    const bothNeg = ra.d < 0 && rb.d < 0;
    return {
      id: 'double-round',
      name: 'Double distributivité',
      result: a * b,
      ...(bothNeg
        ? {
            area: {
              rows: [ra.m, ra.d] as [number, number],
              cols: [rb.m, rb.d] as [number, number],
            },
          }
        : { grid: { rows, cols } }),
      steps: [
        {
          tex: `${tx(a)} \\times ${tx(b)} = (${near(ra.m, ra.d)})(${near(rb.m, rb.d)})`,
          say: 'On arrondit les deux facteurs.',
        },
        {
          tex: `= ${productsTex(rows, cols)}`,
          say: 'Double distributivité : chaque terme de la 1re parenthèse multiplie chaque terme de la 2e.',
        },
        { tex: `= ${sumTex(ps)}`, say: 'On calcule chaque produit, signe compris.' },
        {
          tex: `= ${tx(a * b)}`,
          say: bothNeg
            ? `(−${fmt(-ra.d)}) × (−${fmt(-rb.d)}) = +${fmt(ra.d * rb.d)} : la petite case a été enlevée deux fois (avec chaque bande), on la rajoute une fois.`
            : 'On rassemble.',
        },
      ],
    };
  },
};

const MUL_SQUARE: StrategyDef = {
  id: 'square',
  op: 'mul',
  minRank: 4,
  plan: (a, b) => {
    const r = squareCase(a, b);
    if (!r) return null;
    const d = Math.abs(r.d);
    const plus = r.d > 0;
    return {
      id: 'square',
      name: plus ? 'Carré d’une somme' : 'Carré d’une différence',
      result: a * a,
      ...(plus
        ? { grid: { rows: [r.m, r.d], cols: [r.m, r.d] } }
        : { area: { rows: [r.m, r.d] as [number, number], cols: [r.m, r.d] as [number, number] } }),
      steps: [
        {
          tex: `${tx(a)}^2 = (${near(r.m, r.d)})^2`,
          say: `${fmt(a)} est tout près de ${fmt(r.m)}.`,
        },
        {
          tex: `= ${tx(r.m)}^2 ${plus ? '+' : '-'} 2 \\times ${tx(r.m)} \\times ${d} + ${d}^2`,
          say: plus
            ? `Identité remarquable (a + b)² = a² + 2ab + b², avec a = ${fmt(r.m)} et b = ${d}.`
            : `Identité remarquable (a − b)² = a² − 2ab + b², avec a = ${fmt(r.m)} et b = ${d}.`,
        },
        { tex: `= ${tx(r.m * r.m)} ${plus ? '+' : '-'} ${tx(2 * r.m * d)} + ${d * d}`, say: '' },
        { tex: `= ${tx(a * a)}`, say: '' },
      ],
    };
  },
};

const MUL_DIFF_SQUARES: StrategyDef = {
  id: 'diff-squares',
  op: 'mul',
  minRank: 4,
  plan: (a, b) => {
    const c = diffSquaresCase(a, b);
    if (!c) return null;
    const { m, e } = c;
    // dans l'ordre où les facteurs sont écrits : 52 × 48 = (50 + 2)(50 − 2)
    const da = a - m;
    const db = b - m;
    return {
      id: 'diff-squares',
      name: 'Somme × différence',
      result: a * b,
      grid: { rows: [m, da], cols: [m, db] },
      steps: [
        {
          tex: `${tx(a)} \\times ${tx(b)} = (${near(m, da)})(${near(m, db)})`,
          say: `${fmt(Math.min(a, b))} et ${fmt(Math.max(a, b))} sont de part et d’autre de ${fmt(m)}, à ${fmt(e)} de chaque côté.`,
        },
        {
          tex: `= ${tx(m)}^2 - ${e}^2`,
          say: `Identité remarquable (a + b)(a − b) = a² − b², avec a = ${fmt(m)} et b = ${e}.`,
        },
        { tex: `= ${tx(m * m)} - ${e * e}`, say: '' },
        { tex: `= ${tx(a * b)}`, say: '' },
      ],
    };
  },
};

export const STRATEGIES: readonly StrategyDef[] = [
  ADD_BRIDGE,
  ADD_ROUND_BOTH,
  ADD_ROUND,
  ADD_RANKS,
  SUB_COMPLETE,
  SUB_BRIDGE,
  SUB_ROUND,
  SUB_SHIFT,
  SUB_RANKS,
  MUL_DIFF_SQUARES,
  MUL_SQUARE,
  MUL_QUARTER,
  MUL_DOUBLE_ROUND,
  MUL_ROUND_ONE,
  MUL_SPLIT,
];

/** Les plans possibles pour ce calcul à ce niveau, le conseillé en premier. */
export function plansFor(op: Op, a: number, b: number, rank: number): Plan[] {
  const d = direct(op, a, b);
  if (d) return [d];
  const plans = STRATEGIES.filter((s) => s.op === op && s.minRank <= rank).flatMap((s) => {
    const p = s.plan(a, b, rank);
    return p ? [{ p, prio: 0, idx: STRATEGIES.indexOf(s) }] : [];
  });
  for (const x of plans) {
    x.prio = x.idx * 10;
    if (x.p.id === 'complete')
      x.prio = a - b <= 20 && (x.p.line?.jumps.length ?? 0) > 1 ? -5 : 1000;
    if (x.p.id === 'round-one') {
      const g = x.p.grid!;
      const m = g.rows.length === 2 ? g.rows[0]! : g.cols[0]!;
      if (isPow10(m)) x.prio = STRATEGIES.indexOf(MUL_DOUBLE_ROUND) * 10 - 1;
    }
  }
  return plans.sort((x, y) => x.prio - y.prio).map((x) => x.p);
}

export const result = (op: Op, a: number, b: number) =>
  op === 'add' ? a + b : op === 'sub' ? a - b : a * b;

export const OP_SIGN: Record<Op, string> = { add: '+', sub: '−', mul: '×' };
export const OP_TEX: Record<Op, string> = { add: '+', sub: '-', mul: '\\times' };

/**
 * Un calcul qui met en valeur une stratégie (« nouveau calcul », défi),
 * adapté au niveau. `rng` dans [0 ; 1[.
 */
export function randomCalc(op: Op, rank: number, rng: () => number): { a: number; b: number } {
  // un calcul « de tête » n'a rien à montrer : on retire (rarement plus d'une fois)
  for (let i = 0; i < 20; i++) {
    const c = drawCalc(op, rank, rng);
    if (plansFor(op, c.a, c.b, rank)[0]?.id !== 'direct') return c;
  }
  return op === 'mul' ? { a: 49, b: 19 } : op === 'sub' ? { a: 82, b: 39 } : { a: 39, b: 52 };
}

function drawCalc(op: Op, rank: number, rng: () => number): { a: number; b: number } {
  const int = (lo: number, hi: number) => lo + Math.floor(rng() * (hi - lo + 1));
  /** juste sous une dizaine (… 8, … 9) ou juste au-dessus (… 1, … 2) */
  const nearN = (lo: number, hi: number, below = false) => {
    const m = int(lo, hi) * 10;
    const d = below ? [-2, -1][int(0, 1)]! : [-2, -1, 1, 2][int(0, 3)]!;
    return m + d;
  };
  if (op === 'add') {
    const kind = int(0, 3);
    if (kind === 0) return { a: nearN(2, 9, true), b: nearN(2, 9) };
    if (kind === 1) return { a: nearN(2, 49, true), b: int(12, 99) };
    if (kind === 2) {
      // un chiffre qui fait passer la dizaine : 38 + 7
      const a = int(1, 9) * 10 + int(3, 9);
      return { a, b: int(11 - (a % 10), 9) };
    }
    return { a: int(12, 89), b: int(12, 89) };
  }
  if (op === 'sub') {
    const kind = int(0, 2);
    if (kind === 0) {
      const b = nearN(2, 9, true);
      return { a: b + int(10, 120), b };
    }
    if (kind === 1) {
      const r = int(2, 9) * 10;
      return { a: r + int(1, 9), b: r - int(1, 9) };
    }
    const b = int(12, 49);
    return { a: b + int(10, 120), b };
  }
  const kinds = [
    'split',
    'round-one',
    'quarter',
    ...(rank >= 3 ? ['double'] : []),
    ...(rank >= 4 ? ['square', 'diff'] : []),
  ];
  const kind = kinds[int(0, kinds.length - 1)]!;
  if (kind === 'split')
    return int(0, 1) ? { a: int(12, 98), b: int(3, 9) } : { a: int(12, 39), b: int(11, 19) };
  if (kind === 'round-one')
    return { a: [9, 19, 29, 39, 49, 99, 11, 21, 31, 101][int(0, 9)]!, b: int(12, 48) };
  if (kind === 'quarter') return { a: int(12, 88), b: [5, 25, 50][int(0, 2)]! };
  if (kind === 'double') return { a: nearN(2, 9, true), b: nearN(1, 3, true) };
  if (kind === 'square') {
    const n = nearN(2, 9);
    return { a: n, b: n };
  }
  const m = int(2, 9) * 10;
  const e = int(1, 3);
  return { a: m - e, b: m + e };
}
